import {
  GitCompare,
  Radio,
  GitMerge,
  Timer,
  TrendingUp,
  Clock,
  Hourglass,
  Hash,
  Link2,
} from 'lucide-react'
import type { UniversalNodeSchema } from '../universal-node-schema'
import { UniversalNodeRegistry } from '../universal-node-schema'
import { SESSION_TIME_PRESETS } from './entry-window-schema'
import {
  conditionSummary,
  crossoverSummary,
  mergeSummary,
  switcherEquation,
  switcherSummary,
  timeSignalSummary,
} from '../node-labels'

// Condition Node
const conditionSchema: UniversalNodeSchema = {
  type: 'condition',
  // Chris, 2026-10-28: "A [connect] / Operator [dropdown] / B [connect]", A
  // and B lettered left of their controls. B follows its wire: unwired it is
  // a number box, so there is no Compare Type to pick. No check strip under
  // the rows: only the math nodes keep their formula on the face.
  face: {
    shortName: 'Condition',
    summary: conditionSummary,
    equation: conditionSummary,
    inputControl: 'source',
    sections: [
      { title: '', plain: true, inputs: ['input_1'] },
      { title: '', plain: true, params: ['operator'] },
      { title: '', plain: true, inputs: ['input_2'] },
    ],
  },
  displayName: 'Condition',
  category: 'logic',
  icon: GitCompare,
  description: 'Compares two inputs using selected comparison operator',
  inputHandler: {
    type: 'dual',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'A',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_2',
      displayName: 'B',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      // An empty box is required: never a silent 0. Short enough to fit the
      // box at the node's width (Chris, 2026-10-28).
      emptyText: 'Enter number or connect source',
      discriminatedUnion: {
        staticType: 'number',
        defaultStaticValue: 0,
        parameterName: 'inputBType',
        followsWire: true,
      },
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  // One output, drawn as Signal like the Crossover's (Chris, 2026-10-28: "drop
  // the false ... if a user was to flip the condition then they can do so in
  // the operator"). `displayName` stays True, as stored nodes and the engine
  // payload have it. False stays for the engine and saved wires: retired, it
  // is drawn only while a wire holds it.
  outputs: [
    {
      id: 'true',
      displayName: 'True',
      faceLabel: 'Signal',
      type: 'boolean',
      color: 'green',
    },
    {
      id: 'false',
      displayName: 'False',
      type: 'boolean',
      color: 'red',
      retired: 'Use the opposite operator and wire the Signal instead.',
    },
  ],
  parameters: [
    {
      name: 'operator',
      displayName: 'Operator',
      type: 'select',
      default: '>',
      options: [
        { value: '>', label: '>' },
        { value: '<', label: '<' },
        { value: '>=', label: '>=' },
        { value: '<=', label: '<=' },
        { value: '==', label: '==' },
        { value: '!=', label: '!=' },
      ],
    },
    {
      // Stored, never drawn: B follows its wire (followsWire), and this says
      // which, for saved strategies, EdgeScript and the AI
      // (utils/static-mode.ts followWire keeps it in line).
      name: 'inputBType',
      displayName: 'Input B Type',
      type: 'select',
      hidden: true,
      display: 'segmented',
      default: 'node',
      options: [
        { value: 'node', label: 'Node', icon: Link2 },
        { value: 'static', label: 'Static', icon: Hash },
      ],
    },
  ],
  hasEquation: true,
}

// Merge (And/Or Gate) Node
const mergeSchema: UniversalNodeSchema = {
  type: 'merge',
  displayName: 'Merge (And/Or)',
  category: 'logic',
  icon: GitMerge,
  description: 'Combines multiple boolean signals with AND or OR',
  face: { width: 'wide', inputControl: 'toggle', shortName: 'Merge', summary: mergeSummary },
  inputHandler: {
    type: 'multi',
    // An And/Or Gate needs at least ONE input: AND/OR of a single boolean is a
    // valid pass-through, and the engine's condition_block requires >=1 enabled
    // condition (rule_builders._extract_condition_block). minInputs:2 wrongly
    // rejected a 1-input gate — the 2-operand minimum belongs to the condition
    // node (left vs right), not this gate.
    minInputs: 1,
    maxInputs: 50,
  },
  inputs: [], // Empty array allows dynamic generation of input_1, input_2, etc.
  outputs: [
    {
      id: 'result',
      displayName: 'Result',
      type: 'boolean',
    },
  ],
  parameters: [
    {
      name: 'operator',
      displayName: 'Logic',
      type: 'select',
      default: 'AND',
      options: [
        { value: 'AND', label: 'AND' },
        { value: 'OR', label: 'OR' },
      ],
    },
  ],
}

// Special Switcher Node (multi-handle with letters, dropdown, toggle)
const switcherSchema: UniversalNodeSchema = {
  type: 'switcher',
  displayName: 'Switcher',
  category: 'logic',
  icon: Radio,
  description: 'Routes one of multiple inputs to output',
  inputHandler: {
    type: 'multi',
    minInputs: 2,
    maxInputs: 8,
    reveal: 'progressive',
  },
  inputs: [
    {
      id: 'input_1',
      displayName: 'A',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_2',
      displayName: 'B',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_3',
      displayName: 'C',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_4',
      displayName: 'D',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_5',
      displayName: 'E',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_6',
      displayName: 'F',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_7',
      displayName: 'G',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_8',
      displayName: 'H',
      acceptedTypes: ['number', 'boolean', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities', 'logic'],
      fieldSelection: {
        dynamic: true,
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'output',
      displayName: 'Output',
      type: 'number', // Most common output type for Switcher
    },
  ],
  parameters: [
    {
      name: 'activeInput',
      displayName: 'Active Input',
      type: 'int',
      default: 0,
      min: 0,
      max: 7, // 0-7 for input_1 to input_8
    },
  ],
  hasEquation: true,
  face: {
    width: 'wide',
    renderer: 'switcher',
    // A to H read like every other input: the full-width source, not a pill.
    inputControl: 'source',
    shortName: 'Switch',
    summary: switcherSummary,
    equation: switcherEquation,
  },
}

// Signal Hold Node
const timeoutSchema: UniversalNodeSchema = {
  type: 'timeout',
  displayName: 'Signal Hold',
  category: 'logic',
  icon: Timer,
  description: 'Maintains true signal for specified number of bars',
  face: { inputControl: 'source' },
  inputHandler: {
    type: 'single',
  },
  inputs: [
    {
      id: 'signal',
      displayName: '',
      acceptedTypes: ['boolean'],
      // 'logic' (the category) — not the bare 'condition' node type — so the
      // Signal Hold accepts ANY boolean logic source: a condition, a merge
      // (AND/OR GATE), a crossover, or a timeSignal. Matches the convention
      // used by every other boolean-logic input (e.g. the trade entry). The
      // number-output switcher is still excluded by acceptedTypes: ['boolean'].
      acceptedSources: ['logic'],
    },
  ],
  outputs: [
    {
      id: 'output',
      displayName: 'Output',
      type: 'boolean',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Period',
      type: 'int',
      default: 10,
      min: 1,
      max: 100,
    },
  ],
}

// Value Hold Node
const valueHoldSchema: UniversalNodeSchema = {
  type: 'valueHold',
  displayName: 'Value Hold',
  category: 'logic',
  icon: Hourglass,
  description: 'Holds the value from the last valid signal for up to a max number of bars',
  face: { width: 'wide', inputControl: 'source' },
  inputHandler: {
    type: 'dual',
  },
  inputs: [
    {
      id: 'condition',
      displayName: 'Condition',
      acceptedTypes: ['boolean'],
      acceptedSources: ['logic', 'filters', 'utilities', 'math', 'indicators'],
    },
    {
      id: 'value',
      displayName: 'Value',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['*'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'output',
      displayName: 'Value',
      type: 'number',
    },
  ],
  parameters: [
    {
      name: 'period',
      displayName: 'Max Periods',
      type: 'int',
      default: 10,
      min: 1,
      max: 100,
    },
  ],
}

// Crossover Node
const crossoverSchema: UniversalNodeSchema = {
  type: 'crossover',
  // The Condition's stack (Chris, 2026-10-06): Input A and Input B named
  // above their sources, then how one crosses the other, then the box with
  // the equation alone, like the Condition's.
  face: {
    shortName: 'Cross',
    summary: crossoverSummary,
    equation: crossoverSummary,
    inputControl: 'source',
    sections: [
      { title: '', plain: true, inputs: ['input_1'] },
      { title: '', plain: true, inputs: ['input_2'] },
      { title: '', plain: true, params: ['crossType'] },
    ],
  },
  displayName: 'Crossover',
  category: 'logic',
  icon: TrendingUp,
  description: 'Detects when one value crosses over another value',
  inputHandler: {
    type: 'dual',
  },
  inputs: [
    {
      id: 'input_1',
      faceLabel: 'Input A',
      displayName: 'A',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
    {
      id: 'input_2',
      faceLabel: 'Input B',
      displayName: 'B',
      acceptedTypes: ['number', 'ohlcv'],
      acceptedSources: ['priceData', 'indicators', 'math', 'utilities'],
      fieldSelection: {
        dynamic: true, // Field options come from connected node's outputs
        fallbackOptions: [
          { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' },
          { value: 'low', label: 'Low' },
          { value: 'close', label: 'Close' },
          { value: 'volume', label: 'Volume' },
        ],
        defaultField: 'close',
      },
    },
  ],
  outputs: [
    {
      id: 'signal',
      displayName: 'Signal',
      type: 'boolean',
    },
  ],
  parameters: [
    {
      name: 'crossType',
      displayName: 'Cross Type',
      type: 'select',
      default: 'crosses',
      options: [
        { value: 'crosses', label: 'Crosses' },
        { value: 'crosses-above', label: 'Go Above' },
        { value: 'crosses-below', label: 'Go Below' },
      ],
    },
  ],
  hasEquation: true,
}

// Time Signal Node
const timeSignalSchema: UniversalNodeSchema = {
  type: 'timeSignal',
  face: { shortName: 'Time Signal', summary: timeSignalSummary },
  displayName: 'Time Signal',
  category: 'logic',
  icon: Clock,
  description: 'Outputs true within specified time window, false otherwise',
  inputs: [],
  outputs: [
    {
      id: 'true',
      displayName: 'True',
      type: 'boolean',
      color: 'green',
    },
    {
      id: 'false',
      displayName: 'False',
      type: 'boolean',
      color: 'red',
    },
  ],
  parameters: [
    {
      name: 'startTime',
      displayName: 'Start Time',
      type: 'time',
      default: '09:30',
      timePresets: SESSION_TIME_PRESETS,
    },
    {
      name: 'endTime',
      displayName: 'End Time',
      type: 'time',
      default: '16:00',
      timePresets: SESSION_TIME_PRESETS,
    },
  ],
}

// Export all logic schemas
export const logicSchemas = [
  conditionSchema,
  mergeSchema,
  switcherSchema,
  timeoutSchema,
  valueHoldSchema,
  crossoverSchema,
  timeSignalSchema,
]

// Registration function
export function registerLogicSchemas() {
  // console.log('Registering Logic & Control Node Schemas...');

  logicSchemas.forEach((schema) => {
    UniversalNodeRegistry.register(schema)
  })

  // const schemaNames = logicSchemas.map(s => s.displayName).join(', ');
  // console.log(`Registered ${logicSchemas.length} Logic & Control Node Schemas:`, schemaNames);
}

// Export individual schemas
export {
  conditionSchema,
  mergeSchema,
  switcherSchema,
  timeoutSchema,
  valueHoldSchema,
  crossoverSchema,
  timeSignalSchema,
}
